import React from 'react';
import { TriangleAlert } from 'lucide-react';
import type { ToolInputRefIssue } from './tool-input-ref-issues';

/**
 * What is wrong with a ref, said where the ref is.
 *
 * The product's warning colour rather than the destructive one: a ref that no
 * longer resolves is a thing to fix before this runs, not a destructive act
 * being confirmed — and the row is a notice, not the failure itself.
 *
 * Carries a glyph rather than relying on the colour alone. These sit directly
 * under a token input whose pill reads `time.actual_time` in the same calm grey
 * it always has, so the only thing separating "configured" from "about to write
 * a blank" was one tinted line under a busy form.
 *
 * Shared by the generic field rows and by the controls that place their own, so
 * a broken ref looks the same wherever it is reported.
 */
export const RefIssueNotice: React.FC<{
  readonly issues: readonly ToolInputRefIssue[];
}> = ({ issues }) =>
  issues.length === 0 ? null : (
    <>
      {issues.map((issue) => (
        <div
          key={issue.path}
          // Colour on the row so both the glyph and the text inherit it. The
          // message is a plain span rather than `Text.Caption`, whose baked-in
          // `text-muted-foreground` would be fighting this one for the same
          // specificity with only stylesheet order to settle it.
          className="flex items-start gap-1.5 text-sm text-yellow-800"
          role="alert"
        >
          {/* Nudged onto the text's optical centre by eye. The glyph is a
              triangle with its mass low and a lot of air above the apex, so
              centring its bounding box on the line leaves it reading high —
              the offset is against what it looks like, not what it measures. */}
          <TriangleAlert className="mt-1 size-3.5 shrink-0" />
          <span className="min-w-0">{issue.message}</span>
        </div>
      ))}
    </>
  );
